import React, { useEffect, useState } from "react";
import { Select, Table, Card, Pagination } from "antd";
import "../../../styles/DataTable.css";

const { Option } = Select;

interface DatatableProps {
    columns: any[];
    dataSource: any[];
    Selection?: boolean;
    searchText: string;
    onRow?: (record: any, index?: number) => any;
}

const Datatable: React.FC<DatatableProps> = ({
    columns,
    dataSource,
    Selection,
    searchText,
    onRow,
}) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
    const [Selections, setSelections] = useState<any>(true);
    const [filteredDataSource, setFilteredDataSource] = useState(dataSource);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setSelections(Selection !== undefined ? Selection : true);
    }, [Selection]);

    useEffect(() => {
        const filteredData = dataSource.filter((record) =>
            Object.values(record).some((field) =>
                String(field).toLowerCase().includes(searchText.toLowerCase())
            )
        );
        setFilteredDataSource(filteredData);
        setCurrentPage(1); // Reset to first page on search
    }, [searchText, dataSource]);

    const onSelectChange = (newSelectedRowKeys: any[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const paginatedData = filteredDataSource.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    if (isMobile) {
        return (
            <div className="mobile-card-container">
                <div className="mobile-card-list">
                    {paginatedData.length > 0 ? (
                        paginatedData.map((record, index) => (
                            <Card 
                                key={record.id || index}
                                className="mobile-record-card"
                                onClick={() => {
                                    if (onRow) {
                                        const rowProps = onRow(record, index);
                                        if (rowProps?.onClick) rowProps.onClick();
                                    }
                                }}
                            >
                                {columns.map((col, colIndex) => {
                                    // Skip checkboxes in card view
                                    if (col.key === 'selection' || col.title === '') return null;

                                    const isActions = col.key === 'actions' || col.title === 'Actions';
                                    const value = col.render 
                                        ? col.render(record[col.dataIndex], record) 
                                        : record[col.dataIndex];

                                    if (isActions) {
                                        return (
                                            <div key={col.key || colIndex} className="actions-row">
                                                {value}
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={col.key || colIndex} className="card-row">
                                            <span className="card-label">{col.title}</span>
                                            <span className="card-value">{value}</span>
                                        </div>
                                    );
                                })}
                            </Card>
                        ))
                    ) : (
                        <div className="no-data-mobile">No data found</div>
                    )}
                </div>
                
                <div className="mobile-pagination">
                    <div className="mobile-pagination-info">
                        Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredDataSource.length)} of {filteredDataSource.length} entries
                    </div>
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={filteredDataSource.length}
                        onChange={(page) => setCurrentPage(page)}
                        simple={true}
                        showSizeChanger={false}
                    />
                </div>
            </div>
        );
    }

    return (
        <Table
            className="table table-nowrap datatable"
            rowSelection={Selections ? rowSelection : undefined}
            columns={columns}
            rowHoverable={false}
            dataSource={filteredDataSource}
            onRow={onRow}
            pagination={{
                current: currentPage,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
                pageSize,
                onShowSizeChange: (size) => setPageSize(size),
                total: filteredDataSource.length,
                showTotal: (total) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        Rows per page:
                        <Select
                            value={pageSize}
                            onChange={(value) => setPageSize(value)}
                            style={{ width: 80 }}
                            popupMatchSelectWidth={false}
                        >
                            <Option value={10}>10</Option>
                            <Option value={20}>20</Option>
                            <Option value={30}>30</Option>
                        </Select>
                        of {total} Entries
                    </div>
                ),
            }}
        />
    );
};

export default Datatable;
